import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/apiClient';
import { DOMAIN_AGENT_TYPES } from '@/types/workstream';
import type { WorkstreamDto } from '@/types/workstream';

// Query keys for cache management
export const workstreamKeys = {
  all: ['workstreams'] as const,
  byProject: (projectId: number) => [...workstreamKeys.all, 'project', projectId] as const,
  detail: (id: number) => [...workstreamKeys.all, 'detail', id] as const,
};

export interface UseWorkstreamsByProjectQueryOptions {
  /** Set to a millisecond interval to poll (e.g. while a report is generating), or `false` to disable polling. */
  refetchInterval?: number | false;
}

/**
 * Hook to fetch workstreams for a project, filtered to domain agents only.
 * GET /api/workstreams/project/{projectId}
 */
export const useWorkstreamsByProjectQuery = (
  projectId: number,
  options?: UseWorkstreamsByProjectQueryOptions
) => {
  return useQuery({
    queryKey: workstreamKeys.byProject(projectId),
    queryFn: () => apiClient.getWorkstreamsByProjectId(projectId),
    select: (data: WorkstreamDto[]) =>
      data.filter((ws) => DOMAIN_AGENT_TYPES.includes(ws.agentType)),
    enabled: projectId > 0,
    gcTime: 5 * 60 * 1000,
    refetchInterval: options?.refetchInterval ?? false,
  });
};

/**
 * Hook to fetch a single workstream by ID.
 * GET /api/workstreams/{id}
 */
export const useWorkstreamByIdQuery = (id: number) => {
  return useQuery({
    queryKey: workstreamKeys.detail(id),
    queryFn: () => apiClient.getWorkstreamById(id),
    enabled: id > 0,
    gcTime: 5 * 60 * 1000,
  });
};
